'use client';

import { useState } from 'react';
import { Facebook, Linkedin, Send, Link2, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

interface BlogSocialShareProps {
    shareUrl: string;
    shareTitle: string;
    hasImage: boolean;
    align?: 'left' | 'center' | 'right';
}

export function BlogSocialShare({ shareUrl, shareTitle, hasImage, align = 'center' }: BlogSocialShareProps) {
    const [copied, setCopied] = useState(false);

    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {

        }
    };

    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedTitle = encodeURIComponent(shareTitle);

    const shareLinks = {
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
        twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
        linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
        telegram: `https://t.me/share/url?url=${encodedUrl}&text=${encodedTitle}`,
    };

    const justifyClass = align === 'left' ? 'justify-start' : align === 'right' ? 'justify-end' : 'justify-center';

    return (
        <div className={`flex flex-wrap items-center ${justifyClass} gap-2`}>
            <span
                className={cn(
                    'text-sm font-medium',
                    hasImage 
                        ? 'text-gray-700 dark:text-white/90' 
                        : 'text-gray-700 dark:text-gray-300'
                )}
            >
                Share:
            </span>

            {/* Facebook */}
            <a
                href={shareLinks.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                    'flex items-center justify-center w-9 md:w-10 h-9 md:h-10 rounded-lg transition-all duration-200',
                    hasImage
                        ? 'bg-white/80 dark:bg-white/10 hover:bg-white dark:hover:bg-white/20 text-[#1877F2] dark:text-white backdrop-blur-sm shadow-sm dark:shadow-none'
                        : 'bg-[#1877F2] hover:bg-[#1565D8] text-white'
                )}
                aria-label="Share on Facebook"
            >
                <Facebook className="h-5 w-5" />
            </a>

            {/* X (Twitter) */}
            <a
                href={shareLinks.twitter}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                    'flex items-center justify-center w-9 md:w-10 h-9 md:h-10 rounded-lg transition-all duration-200',
                    hasImage
                        ? 'bg-white/80 dark:bg-white/10 hover:bg-white dark:hover:bg-white/20 text-black dark:text-white backdrop-blur-sm shadow-sm dark:shadow-none'
                        : 'bg-black hover:bg-gray-800 text-white dark:bg-white dark:hover:bg-gray-100 dark:text-black'
                )}
                aria-label="Share on X"
            >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
            </a>

            {/* LinkedIn */}
            <a
                href={shareLinks.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                    'flex items-center justify-center w-9 md:w-10 h-9 md:h-10 rounded-lg transition-all duration-200',
                    hasImage
                        ? 'bg-white/80 dark:bg-white/10 hover:bg-white dark:hover:bg-white/20 text-[#0A66C2] dark:text-white backdrop-blur-sm shadow-sm dark:shadow-none'
                        : 'bg-[#0A66C2] hover:bg-[#004182] text-white'
                )}
                aria-label="Share on LinkedIn"
            >
                <Linkedin className="h-5 w-5" />
            </a>

            {/* Telegram */}
            <a
                href={shareLinks.telegram}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                    'flex items-center justify-center w-9 md:w-10 h-9 md:h-10 rounded-lg transition-all duration-200',
                    hasImage
                        ? 'bg-white/80 dark:bg-white/10 hover:bg-white dark:hover:bg-white/20 text-[#0088cc] dark:text-white backdrop-blur-sm shadow-sm dark:shadow-none'
                        : 'bg-[#0088cc] hover:bg-[#0077b5] text-white'
                )}
                aria-label="Share on Telegram"
            >
                <Send className="h-5 w-5" />
            </a>

            {/* Copy Link */}
            <button
                onClick={handleCopyLink}
                className={cn(
                    'flex items-center gap-2 px-2 md:px-4 py-2 rounded-lg transition-all duration-200',
                    hasImage
                        ? 'bg-white/80 dark:bg-white/10 hover:bg-white dark:hover:bg-white/20 text-gray-700 dark:text-white backdrop-blur-sm shadow-sm dark:shadow-none'
                        : 'bg-gray-200 hover:bg-gray-300 text-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-200'
                )}
                aria-label="Copy link"
            >
                {copied ? (
                    <>
                        <Check className="h-4 w-4" />
                        <span className="text-sm font-medium">Copied!</span>
                    </>
                ) : (
                    <>
                        <Link2 className="h-4 w-4" />
                        <span className="text-sm font-medium">Copy Link</span>
                    </>
                )}
            </button>
        </div>
    );
}

